import React from 'react'
import { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import NS from "../assets/NS_Logo.png";
import { supabase } from '../SupabaseClient';
import { message, Tooltip } from 'antd';

function Hero() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        businessName: "",
        businessType: "",
        email: "",
        phone: "",
        service: "",
    });

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const { data, error } = await supabase
                .from('leads') // Replace with your table name
                .insert([
                    {
                        name: form.name,
                        business_name: form.businessName,
                        business_type: form.businessType,
                        email: form.email,
                        phone: form.phone,
                        service: form.service,
                        created_at: new Date().toISOString()
                    }
                ]);

            if (error) throw error;

            console.log("Form submitted successfully:", data);
            message.success("Thank you! We'll be in touch soon.");

            if (window.gtag) {
                window.gtag('event', 'generate_lead', {
                    method: 'popup_form'
                });
            }

            // Reset form
            setForm({
                name: "",
                businessName: "",
                businessType: "",
                email: "",
                phone: "",
                service: "",
            });

            setOpen(false);
        } catch (error) {
            console.error("Error submitting form:", error);
            message.error("There was an error submitting the form. Please try again.");
        }
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-emerald-600/10" />

            <div className="absolute inset-0 opacity-30">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl animate-float" />
                <div
                    className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl animate-float"
                    style={{ animationDelay: "2s" }}
                />
            </div>

            {/* Fixed Navigation */}
            <nav className="absolute top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 sm:py-6">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <img src={NS} width="150px" alt="NeuroStudio Logo" />
                    </div>
                    <Tooltip title="🚧 Dashboard is under construction" placement="bottom">
                        <button className="px-4 sm:px-6 py-2 sm:py-2.5 bg-white text-slate-900 rounded-full font-semibold text-sm sm:text-base hover:bg-blue-50 transition-all duration-300 transform hover:scale-105">
                            Dashboard
                        </button>
                    </Tooltip>
                </div>
            </nav>

            {/* Hero Content */}
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center pt-20 sm:pt-0">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                    Your AI-Powered
                    <br />
                    <span className="bg-gradient-to-r from-blue-400 via-emerald-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                        Digital Workforce
                    </span>
                </h1>

                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-300 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
                    Transform your business with intelligent AI employees, conversational chatbots,
                    automated backends, and stunning web &amp; app designs.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 px-4">
                    <button
                        className="w-full sm:w-auto group px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-full font-semibold text-base sm:text-lg hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                        onClick={() => setOpen(true)}
                    >
                        <span>Start Building</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>

                    <button
                        className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white rounded-full font-semibold text-base sm:text-lg hover:bg-white/10 transition-all duration-300"
                        onClick={() =>
                            document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
                        }
                    >
                        View Demos
                    </button>
                </div>

                {/* Stats Grid */}
                <div className="mt-12 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-4xl mx-auto px-4">
                    <div className="text-center">
                        <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">100+</div>
                        <div className="text-xs sm:text-sm md:text-base text-slate-400">AI Employees Deployed</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">99.9%</div>
                        <div className="text-xs sm:text-sm md:text-base text-slate-400">Uptime Guarantee</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">24/7</div>
                        <div className="text-xs sm:text-sm md:text-base text-slate-400">Always Available</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">10x</div>
                        <div className="text-xs sm:text-sm md:text-base text-slate-400">Faster Deployment</div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 sm:p-8 w-full max-w-lg relative shadow-2xl max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white hover:text-red-400">
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">Start Your AI Journey</h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                type="text"
                                name="name"
                                placeholder="Your Name"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />

                            <input
                                type="text"
                                name="businessName"
                                placeholder="Business Name"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.businessName}
                                onChange={handleChange}
                            />

                            <input
                                type="text"
                                name="businessType"
                                placeholder="Business Type"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.businessType}
                                onChange={handleChange}
                            />

                            <input
                                type="email"
                                name="email"
                                placeholder="Email (required)"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.email}
                                onChange={handleChange}
                                required
                            />

                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number (optional)"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.phone}
                                onChange={handleChange}
                            />

                            <select
                                name="service"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white"
                                value={form.service}
                                onChange={handleChange}
                                required
                            >
                                <option value="" className="text-slate-900">Select a Service</option>
                                <option value="AI Agent">AI Agent / Employee</option>
                                <option value="Chatbot">Chatbot</option>
                                <option value="Backend Automation">Backend Automation</option>
                                <option value="Web/App Design">Web / App Design</option>
                                <option value="Custom Suite">Custom Suite</option>
                            </select>

                            <button
                                type="submit"
                                className="w-full py-3 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-lg text-white font-semibold hover:opacity-90"
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />
        </section>
    );
}

export default Hero
